import { useCallback, useEffect, useMemo, useState } from "react";
import { Award, Edit3, Gift, Plus, Save, Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const defaultSettings = {
  leaderboard_rewards_enabled: true,
  leaderboard_locked_message: "",
};

const emptyReward = {
  rank: 1,
  title: "Weekly champion",
  description: "Highest paid depositor this week.",
  reward_type: "freeplay",
  reward_amount: 25,
  active: true,
};

const emptyAchievement = {
  title: "First deposit",
  icon: "trophy",
  description: "Make an approved deposit in this club.",
  goal: "deposit-count",
  target_value: 1,
  reward_credits: 5,
  claims_per_player: 1,
  require_phone: false,
  active: true,
  vendor_scoped: true,
};

const ACHIEVEMENT_GOALS = [
  ["signup", "Signup"],
  ["deposit-count", "Deposit count"],
  ["deposit-total", "Deposit total"],
  ["game-load-count", "Game load count"],
  ["game-load-total", "Game load total"],
  ["referrals", "Referrals"],
];

const inputClass = "w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-[#D4AF37]";

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-label={label}
      className={`relative h-8 w-14 rounded-full transition-colors ${checked ? "bg-[#D4AF37]" : "bg-white/10"}`}
    >
      <span className={`absolute top-1 h-6 w-6 rounded-full bg-black transition-transform ${checked ? "translate-x-7" : "translate-x-1"}`} />
    </button>
  );
}

export default function AdminRewards() {
  const [settings, setSettings] = useState(defaultSettings);
  const [rewards, setRewards] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [preview, setPreview] = useState([]);
  const [weekStart, setWeekStart] = useState("");
  const [form, setForm] = useState(emptyReward);
  const [achievementForm, setAchievementForm] = useState(emptyAchievement);
  const [editingId, setEditingId] = useState("");
  const [editingAchievementId, setEditingAchievementId] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingReward, setSavingReward] = useState(false);
  const [savingAchievement, setSavingAchievement] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/rewards");
    setSettings({ ...defaultSettings, ...(data.settings || {}) });
    setRewards(data.leaderboard_rewards || []);
    setAchievements(data.achievements || []);
    setPreview(data.preview || []);
    setWeekStart(data.week_start || "");
  }, []);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [load]);

  const activeRewards = useMemo(() => rewards.filter((row) => row.active), [rewards]);
  const activeAchievements = useMemo(() => achievements.filter((row) => row.active), [achievements]);

  const updateSettings = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const updateAchievementForm = (key, value) => {
    setAchievementForm((current) => ({ ...current, [key]: value }));
  };

  const applyPayload = (data) => {
    setSettings({ ...defaultSettings, ...(data.settings || {}) });
    setRewards(data.leaderboard_rewards || []);
    setAchievements(data.achievements || []);
    setPreview(data.preview || []);
    setWeekStart(data.week_start || "");
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setSavingSettings(true);
    try {
      const payload = {
        leaderboard_rewards_enabled: Boolean(settings.leaderboard_rewards_enabled),
        leaderboard_locked_message: String(settings.leaderboard_locked_message || "").slice(0, 300),
      };
      const { data } = await api.put("/admin/rewards/settings", payload);
      applyPayload(data);
      toast.success("Reward settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  const saveReward = async (event) => {
    event.preventDefault();
    const payload = {
      rank: Number(form.rank || 1),
      title: String(form.title || "").trim(),
      description: String(form.description || "").trim(),
      reward_type: form.reward_type || "freeplay",
      reward_amount: Number(form.reward_amount || 0),
      active: Boolean(form.active),
    };
    if (!payload.title) {
      toast.error("Reward title is required");
      return;
    }
    setSavingReward(true);
    try {
      const request = editingId
        ? api.put(`/admin/rewards/leaderboard/${editingId}`, payload)
        : api.post("/admin/rewards/leaderboard", payload);
      const { data } = await request;
      applyPayload(data);
      setForm(emptyReward);
      setEditingId("");
      toast.success(editingId ? "Reward updated" : "Reward added");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingReward(false);
    }
  };

  const editReward = (row) => {
    setEditingId(row.id);
    setForm({
      rank: row.rank || 1,
      title: row.title || "",
      description: row.description || "",
      reward_type: row.reward_type || "freeplay",
      reward_amount: row.reward_amount || 0,
      active: row.active !== false,
    });
  };

  const deleteReward = async (row) => {
    if (!window.confirm(`Delete Top ${row.rank} reward?`)) return;
    try {
      const { data } = await api.delete(`/admin/rewards/leaderboard/${row.id}`);
      applyPayload(data);
      if (editingId === row.id) {
        setEditingId("");
        setForm(emptyReward);
      }
      toast.success("Reward deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancelEdit = () => {
    setEditingId("");
    setForm(emptyReward);
  };

  const saveAchievement = async (event) => {
    event.preventDefault();
    const payload = {
      title: String(achievementForm.title || "").trim(),
      icon: String(achievementForm.icon || "trophy").trim(),
      description: String(achievementForm.description || "").trim(),
      goal: achievementForm.goal || "custom",
      target_value: Number(achievementForm.target_value || 0),
      reward_credits: Number(achievementForm.reward_credits || 0),
      claims_per_player: Number(achievementForm.claims_per_player || 1),
      require_phone: Boolean(achievementForm.require_phone),
      active: Boolean(achievementForm.active),
      vendor_scoped: Boolean(achievementForm.vendor_scoped),
    };
    if (!payload.title) {
      toast.error("Achievement title is required");
      return;
    }
    setSavingAchievement(true);
    try {
      const request = editingAchievementId
        ? api.put(`/admin/rewards/achievements/${editingAchievementId}`, payload)
        : api.post("/admin/rewards/achievements", payload);
      const { data } = await request;
      applyPayload(data);
      setAchievementForm(emptyAchievement);
      setEditingAchievementId("");
      toast.success(editingAchievementId ? "Achievement updated" : "Achievement added");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingAchievement(false);
    }
  };

  const editAchievement = (row) => {
    setEditingAchievementId(row.id);
    setAchievementForm({
      title: row.title || "",
      icon: row.icon || "trophy",
      description: row.description || "",
      goal: row.goal || "custom",
      target_value: row.target_value ?? 1,
      reward_credits: row.reward_credits ?? 0,
      claims_per_player: row.claims_per_player || 1,
      require_phone: Boolean(row.require_phone),
      active: row.active !== false,
      vendor_scoped: row.vendor_scoped !== false,
    });
  };

  const deleteAchievement = async (row) => {
    if (!window.confirm(`Delete ${row.title || "achievement"}?`)) return;
    try {
      const { data } = await api.delete(`/admin/rewards/achievements/${row.id}`);
      applyPayload(data);
      if (editingAchievementId === row.id) {
        setEditingAchievementId("");
        setAchievementForm(emptyAchievement);
      }
      toast.success("Achievement deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleRewardActive = async (row) => {
    try {
      const { data } = await api.put(`/admin/rewards/leaderboard/${row.id}`, {
        rank: Number(row.rank || 1),
        title: String(row.title || "").trim(),
        description: String(row.description || "").trim(),
        reward_type: row.reward_type || "freeplay",
        reward_amount: Number(row.reward_amount || 0),
        active: row.active === false,
      });
      applyPayload(data);
      toast.success(row.active === false ? "Reward turned on" : "Reward turned off");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancelAchievementEdit = () => {
    setEditingAchievementId("");
    setAchievementForm(emptyAchievement);
  };

  return (
    <div className="mx-auto w-full max-w-7xl p-3 text-slate-200 sm:p-6 md:p-8">
      <header className="mb-6">
        <div className="flex items-center gap-2">
          <Trophy className="h-6 w-6 text-[#D4AF37]" />
          <h1 className="text-2xl font-bold tracking-tight text-white">Rewards</h1>
        </div>
        <p className="mt-1 max-w-4xl text-sm text-slate-400">
          Set weekly freeplay prizes for top paid depositors. Players can claim them on Saturdays, and claimed reward freeplay redeems $20-$50 with 40% credited to wallet and 60% deducted.
        </p>
      </header>

      <form onSubmit={saveSettings} className="mb-6 rounded-2xl border border-[#3A3216]/80 bg-[#101010] p-4 sm:p-6">
        <div className="grid gap-5 lg:grid-cols-[1fr_1fr_auto] lg:items-start">
          <div className="rounded-xl border border-white/10 bg-black/20 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-white">Leaderboard rewards</h2>
                <p className="mt-1 text-sm text-slate-400">When enabled, reward badges show to players and claims open every Saturday.</p>
              </div>
              <Toggle
                checked={settings.leaderboard_rewards_enabled}
                onChange={(value) => updateSettings("leaderboard_rewards_enabled", value)}
                label="Enable leaderboard rewards"
              />
            </div>
          </div>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-white">Locked / off message</span>
            <textarea
              value={settings.leaderboard_locked_message}
              maxLength={300}
              onChange={(event) => updateSettings("leaderboard_locked_message", event.target.value)}
              className={`${inputClass} min-h-24 resize-y`}
              placeholder="Leaderboard rewards are paused this week."
            />
          </label>

          <button
            disabled={savingSettings}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[#D4AF37] px-6 text-sm font-bold text-black hover:bg-[#F1C40F] disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {savingSettings ? "Saving..." : "Save settings"}
          </button>
        </div>
      </form>

      <section className="mb-6 grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <form onSubmit={saveAchievement} className="rounded-2xl border border-[#3A3216]/80 bg-[#101010] p-4 sm:p-6">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-white">{editingAchievementId ? "Edit achievement" : "Add achievement"}</h2>
              <p className="mt-1 text-sm text-slate-400">Goal, reward, repeat, and phone requirement.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs font-black text-[#F8E08E]">
                Vendor scoped
              </span>
              {editingAchievementId && (
                <button type="button" onClick={cancelAchievementEdit} className="rounded-xl border border-white/10 px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5">
                  Cancel edit
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white">Title</span>
              <input value={achievementForm.title} onChange={(event) => updateAchievementForm("title", event.target.value)} className={inputClass} maxLength={80} required />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white">Icon</span>
              <input value={achievementForm.icon} onChange={(event) => updateAchievementForm("icon", event.target.value)} className={inputClass} maxLength={40} placeholder="trophy" />
            </label>
          </div>

          <label className="mt-4 block">
            <span className="mb-2 block text-sm font-semibold text-white">Description</span>
            <textarea
              value={achievementForm.description}
              maxLength={300}
              onChange={(event) => updateAchievementForm("description", event.target.value)}
              className={`${inputClass} min-h-24 resize-y`}
              placeholder="Make an approved deposit in this club."
            />
          </label>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white">Goal</span>
              <select value={achievementForm.goal} onChange={(event) => updateAchievementForm("goal", event.target.value)} className={inputClass}>
                {ACHIEVEMENT_GOALS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white">Target value</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={achievementForm.target_value}
                onChange={(event) => updateAchievementForm("target_value", event.target.value)}
                className={inputClass}
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white">Reward credits</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={achievementForm.reward_credits}
                onChange={(event) => updateAchievementForm("reward_credits", event.target.value)}
                className={inputClass}
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white">Claims per player</span>
              <input
                type="number"
                min="1"
                step="1"
                value={achievementForm.claims_per_player}
                onChange={(event) => updateAchievementForm("claims_per_player", event.target.value)}
                className={inputClass}
              />
            </label>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-5">
            <label className="inline-flex items-center gap-3 text-sm font-semibold text-white">
              <input
                type="checkbox"
                checked={achievementForm.require_phone}
                onChange={(event) => updateAchievementForm("require_phone", event.target.checked)}
                className="h-5 w-5 accent-[#D4AF37]"
              />
              Require phone
            </label>
            <label className="inline-flex items-center gap-3 text-sm font-semibold text-white">
              <input
                type="checkbox"
                checked={achievementForm.active}
                onChange={(event) => updateAchievementForm("active", event.target.checked)}
                className="h-5 w-5 accent-[#D4AF37]"
              />
              Active
            </label>
            <label className="inline-flex items-center gap-3 text-sm font-semibold text-white">
              <input
                type="checkbox"
                checked={achievementForm.vendor_scoped}
                onChange={(event) => updateAchievementForm("vendor_scoped", event.target.checked)}
                className="h-5 w-5 accent-[#D4AF37]"
              />
              Vendor scoped
            </label>
            <button
              disabled={savingAchievement}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F2A100] px-6 py-3 text-sm font-bold text-black hover:bg-[#FFC533] disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              {savingAchievement ? "Saving..." : editingAchievementId ? "Save achievement" : "Add rule"}
            </button>
          </div>
        </form>

        <section className="overflow-hidden rounded-2xl border border-[#3A3216]/80 bg-[#101010]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <Award className="mt-1 h-5 w-5 text-[#D4AF37]" />
              <div>
                <h2 className="text-lg font-bold text-white">Achievements</h2>
                <p className="mt-1 text-sm text-slate-400">{activeAchievements.length} active rule{activeAchievements.length === 1 ? "" : "s"}.</p>
              </div>
            </div>
          </div>
          <div className="max-h-[520px] overflow-auto">
            {achievements.map((row) => (
              <div key={row.id} className="border-b border-white/5 p-4 last:border-b-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-white">{row.title}</span>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${row.active ? "bg-emerald-500/10 text-emerald-300" : "bg-white/10 text-slate-400"}`}>
                        {row.active ? "Active" : "Off"}
                      </span>
                      {row.require_phone && (
                        <span className="rounded-full border border-sky-400/30 px-2.5 py-1 text-[11px] font-bold text-sky-200">Phone</span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-400">{row.description || "-"}</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                      <span>{goalLabel(row.goal)}</span>
                      <span>Target {prettyNumber(row.target_value)}</span>
                      <span>{money(row.reward_credits)} FP</span>
                      <span>{row.claims_per_player || 1} claim{Number(row.claims_per_player || 1) === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button type="button" onClick={() => editAchievement(row)} className="rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-slate-200 hover:bg-white/5">
                      Edit
                    </button>
                    <button type="button" onClick={() => deleteAchievement(row)} className="rounded-lg border border-red-500/30 px-3 py-2 text-xs font-bold text-red-300 hover:bg-red-500/10">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {!loading && achievements.length === 0 && (
              <div className="p-10 text-center text-sm text-slate-500">No achievements configured yet.</div>
            )}
            {loading && achievements.length === 0 && (
              <div className="p-10 text-center text-sm text-slate-500">Loading achievements...</div>
            )}
          </div>
        </section>
      </section>

      <section className="mb-6 grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <form onSubmit={saveReward} className="rounded-2xl border border-[#3A3216]/80 bg-[#101010] p-4 sm:p-6">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-white">{editingId ? "Edit leaderboard reward" : "Add leaderboard reward"}</h2>
              <p className="mt-1 text-sm text-slate-400">Use Top 1, Top 2, Top 3 for weekly depositor prizes.</p>
            </div>
            {editingId && (
              <button type="button" onClick={cancelEdit} className="rounded-xl border border-white/10 px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5">
                Cancel edit
              </button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white">Rank</span>
              <select value={form.rank} onChange={(event) => updateForm("rank", event.target.value)} className={inputClass}>
                {Array.from({ length: 10 }, (_, index) => index + 1).map((rank) => (
                  <option key={rank} value={rank}>Top {rank}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white">Reward type</span>
              <select value={form.reward_type} onChange={(event) => updateForm("reward_type", event.target.value)} className={inputClass}>
                <option value="freeplay">Freeplay</option>
                <option value="wallet">Wallet bonus</option>
                <option value="custom">Custom / manual</option>
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white">Title</span>
              <input value={form.title} onChange={(event) => updateForm("title", event.target.value)} className={inputClass} maxLength={80} required />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white">Reward amount ($)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.reward_amount}
                onChange={(event) => updateForm("reward_amount", event.target.value)}
                className={inputClass}
              />
            </label>
          </div>

          <label className="mt-4 block">
            <span className="mb-2 block text-sm font-semibold text-white">Description</span>
            <textarea
              value={form.description}
              maxLength={240}
              onChange={(event) => updateForm("description", event.target.value)}
              className={`${inputClass} min-h-24 resize-y`}
              placeholder="Highest paid depositor this week gets this bonus."
            />
          </label>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <label className="inline-flex items-center gap-3 text-sm font-semibold text-white">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) => updateForm("active", event.target.checked)}
                className="h-5 w-5 accent-[#D4AF37]"
              />
              Active
            </label>
            <button
              disabled={savingReward}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F2A100] px-6 py-3 text-sm font-bold text-black hover:bg-[#FFC533] disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              {savingReward ? "Saving..." : editingId ? "Save reward" : "Add reward"}
            </button>
          </div>
        </form>

        <section className="rounded-2xl border border-[#3A3216]/80 bg-[#101010] p-4 sm:p-6">
          <div className="mb-4 flex items-center gap-2">
            <Gift className="h-5 w-5 text-[#D4AF37]" />
            <h2 className="text-xl font-bold text-white">This week preview</h2>
          </div>
          <p className="mb-4 text-xs text-slate-500">Week starts {formatDate(weekStart)}.</p>
          <div className="space-y-3">
            {preview.slice(0, 5).map((row) => (
              <div key={`${row.username}-${row.rank}`} className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-xl font-black text-[#D4AF37]">#{row.rank}</span>
                      <span className="truncate font-bold text-white">{row.username}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-400">{moneyFromUnits(row.weekly_load_coins)} paid deposits this week</p>
                  </div>
                  {row.reward && (
                    <span className="shrink-0 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs font-black text-[#F8E08E]">
                      {row.reward.label}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {!loading && preview.length === 0 && (
              <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-slate-500">
                No paid depositors this week yet.
              </div>
            )}
          </div>
        </section>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#3A3216]/80 bg-[#101010]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-4 sm:p-5">
          <div>
            <h2 className="text-lg font-bold text-white">Leaderboard rewards</h2>
            <p className="mt-1 text-sm text-slate-400">{activeRewards.length} active reward{activeRewards.length === 1 ? "" : "s"}.</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="text-left text-xs font-bold uppercase tracking-wider text-slate-400">
              <tr className="border-b border-white/5">
                <th className="px-4 py-4">Rank</th>
                <th className="px-4 py-4">Reward</th>
                <th className="px-4 py-4">Type</th>
                <th className="px-4 py-4 text-right">Amount</th>
                <th className="px-4 py-4">Status</th>
                <th className="px-4 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rewards.map((row) => (
                <tr key={row.id} className="border-t border-white/5">
                  <td className="px-4 py-4 font-display text-xl font-black text-[#D4AF37]">Top {row.rank}</td>
                  <td className="px-4 py-4">
                    <div className="font-semibold text-white">{row.title}</div>
                    <div className="max-w-xl text-xs text-slate-500">{row.description || "-"}</div>
                  </td>
                  <td className="px-4 py-4 capitalize text-slate-300">{rewardTypeLabel(row.reward_type)}</td>
                  <td className="px-4 py-4 text-right font-mono text-[#F8E08E]">{money(row.reward_amount)}</td>
                  <td className="px-4 py-4">
                    <button
                      type="button"
                      onClick={() => toggleRewardActive(row)}
                      className={`inline-flex min-w-24 items-center justify-center rounded-full px-3 py-1 text-xs font-bold transition hover:scale-[1.02] ${
                        row.active
                          ? "bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/20"
                          : "bg-white/10 text-slate-400 ring-1 ring-white/10"
                      }`}
                      title={row.active ? "Turn this leaderboard reward off" : "Turn this leaderboard reward on"}
                    >
                      {row.active ? "Active - tap off" : "Off - tap on"}
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => editReward(row)} className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-slate-200 hover:bg-white/5">
                        <Edit3 className="h-3.5 w-3.5" />
                        Edit
                      </button>
                      <button onClick={() => deleteReward(row)} className="inline-flex items-center gap-2 rounded-lg border border-red-500/30 px-3 py-2 text-xs font-bold text-red-300 hover:bg-red-500/10">
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && rewards.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">No rewards configured yet.</td>
                </tr>
              )}
              {loading && rewards.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">Loading rewards...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function moneyFromUnits(value) {
  return `$${((Number(value) || 0) / 1000).toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US");
}

function rewardTypeLabel(value) {
  if (value === "wallet") return "Wallet bonus";
  if (value === "custom") return "Custom";
  return "Freeplay";
}

function goalLabel(value) {
  return ACHIEVEMENT_GOALS.find(([key]) => key === value)?.[1] || "Custom";
}

function prettyNumber(value) {
  const number = Number(value || 0);
  return Number.isInteger(number) ? String(number) : number.toFixed(2);
}
